"use client";

import { DiscountCodeInput } from "@/components/discount-code-input";
import { useCheckout } from "@stripe/react-stripe-js/checkout";
import { stripeAmountToPrice } from "@/lib/utils/currencies";
import { Button } from "@/components/catalyst/button";
import { Field } from "./catalyst/fieldset";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface CheckoutPromotionCodeProps {
  isDisabled?: boolean;
  originalPrice: number;
  onChange: (price: number) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function CheckoutPromotionCode({
  isDisabled,
  originalPrice,
  onChange,
  onSuccess,
  onError,
}: CheckoutPromotionCodeProps) {
  const t = useTranslations("CheckoutPromoCode");
  const checkoutState = useCheckout();

  const [promoCode, setPromoCode] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;

    if (checkoutState.type !== "success") {
      onError(t("applyError"));
      return;
    }

    setIsApplying(true);

    try {
      const result = await checkoutState.checkout.applyPromotionCode(code);
      if (result.type === "error") {
        onError(result.error.message);
        return;
      }

      const newPrice = stripeAmountToPrice(
        result.session.total.total.minorUnitsAmount,
      );

      onChange(newPrice);

      setAppliedCode(code);
      onSuccess(t("applySuccess"));
    } catch (err) {
      onError(
        err instanceof Error ? err.message : t("applyError"),
      );
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemovePromo = async () => {
    setIsApplying(true);
    if (checkoutState.type !== "success") {
      onError(t("removeError"));
      return;
    }

    try {
      const result = await checkoutState.checkout.removePromotionCode();
      if (result.type === "error") {
        onError(result.error.message);
        return;
      }

      onChange(originalPrice);
      setAppliedCode(null);
      setPromoCode("");
      onSuccess(t("removeSuccess"));
    } catch (err) {
      onError(
        err instanceof Error ? err.message : t("removeError"),
      );
    } finally {
      setIsApplying(false);
    }
  };

  const isDiscountCodeDisabled = isApplying || isDisabled;
  const isButtonDisabled = isDiscountCodeDisabled || !promoCode;

  return (
    <div className="space-y-2">
      {appliedCode ? (
        <div className="flex items-center gap-3">
          <span className="rounded bg-zinc-100 px-2 py-1 font-mono text-sm dark:bg-zinc-800">
            {appliedCode}
          </span>
          <Button
            type="button"
            color="red"
            onClick={handleRemovePromo}
            disabled={isButtonDisabled}
          >
            {isApplying ? t("removing") : t("remove")}
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 items-end">
          <Field className="flex-1">
            <DiscountCodeInput
              name="promotionCode"
              placeholder={t("enterCode")}
              value={promoCode}
              invalid={false}
              onChange={setPromoCode}
              disabled={isDiscountCodeDisabled}
            />
          </Field>
          <Button
            onClick={handleApplyPromo}
            disabled={isButtonDisabled}
            color="purple"
          >
            {isApplying ? t("applying") : t("apply")}
          </Button>
        </div>
      )}
    </div>
  );
}
